import { motion } from 'framer-motion';
import {
  ArrowRight,
  BookOpen,
  CheckCircle,
  Clock,
  Search,
  Target,
  TrendingUp,
  X,
} from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import LanguageIcon, { detectLanguage } from '../components/LanguageIcon';
import { useAuth } from '../contexts/AuthContext';
import { formatExerciseTitle } from '../utils/formatTitle';
import { getApiUrl } from '../utils/apiUrl';

interface Exercise {
  id: string;
  title: string;
  description: string;
  category: string;
  difficulty: string;
  estimated_time: number;
  tags: string[];
}

interface UserProgress {
  id: string;
  exercise_id: string;
  status: string;
  completed_steps: any[];
  exercises: Exercise;
}

const LOAD_TIMEOUT_MS = 15000;

const Dashboard: React.FC = () => {
  const { user, getAuthToken } = useAuth();
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [userProgress, setUserProgress] = useState<UserProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState<string[]>(
    []
  );
  const [selectedTags, setSelectedTags] = useState<string[]>([]);

  const apiUrl = getApiUrl();

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setFetchError(null);

    const fetchWithTimeout = async <T,>(
      url: string,
      options: RequestInit & { timeoutMs?: number } = {}
    ): Promise<T> => {
      const { timeoutMs = LOAD_TIMEOUT_MS, ...fetchOpts } = options;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
      try {
        const res = await fetch(url, { ...fetchOpts, signal: controller.signal });
        clearTimeout(timeoutId);
        if (!res.ok) throw new Error(`API returned ${res.status}`);
        return res.json();
      } catch (e) {
        clearTimeout(timeoutId);
        if (e instanceof Error && e.name === 'AbortError') {
          throw new Error('Request timed out');
        }
        throw e;
      }
    };

    const fetchExercises = async () => {
      const data = await fetchWithTimeout<{ exercises?: Exercise[] }>(
        `${apiUrl}/api/exercises`
      );
      return data.exercises || [];
    };

    const fetchProgress = async () => {
      const token = await getAuthToken();
      const data = await fetchWithTimeout<{ progress?: UserProgress[] }>(
        `${apiUrl}/api/progress`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      return data.progress || [];
    };

    const withTimeout = <T,>(p: Promise<T>, ms: number) =>
      Promise.race([
        p,
        new Promise<T>((_, r) =>
          setTimeout(() => r(new Error('Request timed out')), ms)
        ),
      ]);

    try {
      const [exercisesResult, progressResult] = await Promise.allSettled([
        withTimeout(fetchExercises(), LOAD_TIMEOUT_MS),
        withTimeout(fetchProgress(), LOAD_TIMEOUT_MS),
      ]);

      if (exercisesResult.status === 'fulfilled') {
        setExercises(exercisesResult.value);
      } else {
        console.error('Exercises fetch failed:', exercisesResult.reason);
        setFetchError(
          'Unable to load exercises. Check that the API is running on port 3001 and try again.'
        );
      }

      if (progressResult.status === 'fulfilled') {
        setUserProgress(progressResult.value);
      } else {
        console.error('Progress fetch failed:', progressResult.reason);
      }
    } catch (err) {
      console.error('Dashboard load error:', err);
      setFetchError('Unable to load dashboard. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [apiUrl, getAuthToken]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const getProgressStats = () => {
    const completed = userProgress.filter(p => p.status === 'completed').length;
    const inProgress = userProgress.filter(
      p => p.status === 'in_progress'
    ).length;
    const total = exercises.length;
    const completionRate =
      total > 0 ? Math.round((completed / total) * 100) : 0;

    return { completed, inProgress, total, completionRate };
  };

  const getFilteredExercises = () => {
    return exercises.filter(exercise => {
      const formattedTitle = formatExerciseTitle(exercise.title);
      const matchesSearch =
        formattedTitle.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exercise.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        exercise.description.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesCategory =
        selectedCategories.length === 0 ||
        selectedCategories.includes(exercise.category);

      const matchesDifficulty =
        selectedDifficulties.length === 0 ||
        selectedDifficulties.includes(exercise.difficulty);

      const matchesTags =
        selectedTags.length === 0 ||
        (Array.isArray(exercise.tags) &&
          selectedTags.every(t => exercise.tags.includes(t)));

      return (
        matchesSearch && matchesCategory && matchesDifficulty && matchesTags
      );
    });
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-yellow-100 text-yellow-800';
      case 'advanced':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className='w-5 h-5 text-green-500' />;
      case 'in_progress':
        return <Clock className='w-5 h-5 text-blue-500' />;
      default:
        return <BookOpen className='w-5 h-5 text-gray-400' />;
    }
  };

  const stats = getProgressStats();
  const filteredExercises = getFilteredExercises();
  const categories = [...new Set(exercises.map(e => e.category))];
  const difficulties = ['beginner', 'intermediate', 'advanced'];
  const tags = [...new Set(exercises.flatMap(e => e.tags || []))];

  const toggleInArray = (
    value: string,
    arr: string[],
    setArr: (v: string[]) => void
  ) => {
    setArr(
      arr.includes(value) ? arr.filter(v => v !== value) : [...arr, value]
    );
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setSelectedCategories([]);
    setSelectedDifficulties([]);
    setSelectedTags([]);
  };

  const removeFilter = (
    type: 'category' | 'difficulty' | 'tag',
    value: string
  ) => {
    if (type === 'category')
      setSelectedCategories(selectedCategories.filter(v => v !== value));
    if (type === 'difficulty')
      setSelectedDifficulties(selectedDifficulties.filter(v => v !== value));
    if (type === 'tag') setSelectedTags(selectedTags.filter(v => v !== value));
  };

  if (loading) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center'>
          <div className='w-16 h-16 mx-auto mb-4'>
            <div className='hexagon bg-gradient-to-r from-hexagon to-hexagon-dark animate-pulse'></div>
          </div>
          <p className='text-gray-600'>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  if (fetchError && exercises.length === 0) {
    return (
      <div className='min-h-screen flex items-center justify-center'>
        <div className='text-center max-w-md'>
          <p className='text-gray-600 mb-4'>{fetchError}</p>
          <p className='text-sm text-gray-500 mb-6'>
            Make sure the API server is running:{' '}
            <code className='bg-gray-100 px-2 py-1 rounded'>
              cd api && npm run dev
            </code>
          </p>
          <button
            onClick={loadDashboard}
            className='hexagon-button px-6 py-3'
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='space-y-8'>
      {/* Welcome Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className='text-center'
      >
        <h1 className='text-3xl lg:text-4xl font-bold text-gray-900 mb-4'>
          Welcome back, {user?.email?.split('@')[0]}! 👋
        </h1>
        <p className='text-xl text-gray-600'>
          Continue your learning journey with these personalized recommendations
        </p>
      </motion.div>

      {/* Progress Stats */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className='grid grid-cols-1 md:grid-cols-4 gap-6'
      >
        <div className='glass-effect rounded-2xl p-6 text-center'>
          <div className='w-16 h-16 mx-auto mb-4'>
            <div className='hexagon bg-gradient-to-r from-green-500 to-green-600 flex items-center justify-center'>
              <CheckCircle className='w-8 h-8 text-white' />
            </div>
          </div>
          <div className='text-2xl font-bold text-gray-900'>
            {stats.completed}
          </div>
          <div className='text-gray-600'>Completed</div>
        </div>

        <div className='glass-effect rounded-2xl p-6 text-center'>
          <div className='w-16 h-16 mx-auto mb-4'>
            <div className='hexagon bg-gradient-to-r from-blue-500 to-blue-600 flex items-center justify-center'>
              <Clock className='w-8 h-8 text-white' />
            </div>
          </div>
          <div className='text-2xl font-bold text-gray-900'>
            {stats.inProgress}
          </div>
          <div className='text-gray-600'>In Progress</div>
        </div>

        <div className='glass-effect rounded-2xl p-6 text-center'>
          <div className='w-16 h-16 mx-auto mb-4'>
            <div className='hexagon bg-gradient-to-r from-purple-500 to-purple-600 flex items-center justify-center'>
              <Target className='w-8 h-8 text-white' />
            </div>
          </div>
          <div className='text-2xl font-bold text-gray-900'>{stats.total}</div>
          <div className='text-gray-600'>Total Available</div>
        </div>

        <div className='glass-effect rounded-2xl p-6 text-center'>
          <div className='w-16 h-16 mx-auto mb-4'>
            <div className='hexagon bg-gradient-to-r from-hexagon to-accent-500 flex items-center justify-center'>
              <TrendingUp className='w-8 h-8 text-white' />
            </div>
          </div>
          <div className='text-2xl font-bold text-gray-900'>
            {stats.completionRate}%
          </div>
          <div className='text-gray-600'>Completion Rate</div>
        </div>
      </motion.div>

      {/* Search and Filters */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
        className='glass-effect rounded-2xl p-6'
      >
        <div className='flex flex-col gap-4'>
          <div className='relative'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5' />
            <input
              type='text'
              placeholder='Search exercises...'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className='w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-hexagon focus:border-transparent'
            />
          </div>

          {/* Active filter pills */}
          {(selectedCategories.length > 0 ||
            selectedDifficulties.length > 0 ||
            selectedTags.length > 0) && (
            <div className='flex items-center flex-wrap gap-2'>
              {selectedCategories.map(cat => (
                <span
                  key={`cat-${cat}`}
                  className='inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm bg-hexagon/10 text-hexagon'
                >
                  {cat}
                  <button
                    onClick={() => removeFilter('category', cat)}
                    className='hover:text-hexagon-dark'
                    aria-label={`Remove ${cat} filter`}
                  >
                    <X className='w-4 h-4' />
                  </button>
                </span>
              ))}
              {selectedDifficulties.map(diff => (
                <span
                  key={`diff-${diff}`}
                  className='inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm bg-hexagon/10 text-hexagon'
                >
                  {diff}
                  <button
                    onClick={() => removeFilter('difficulty', diff)}
                    className='hover:text-hexagon-dark'
                    aria-label={`Remove ${diff} filter`}
                  >
                    <X className='w-4 h-4' />
                  </button>
                </span>
              ))}
              {selectedTags.map(tag => (
                <span
                  key={`tag-${tag}`}
                  className='inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm bg-hexagon/10 text-hexagon'
                >
                  {tag}
                  <button
                    onClick={() => removeFilter('tag', tag)}
                    className='hover:text-hexagon-dark'
                    aria-label={`Remove ${tag} filter`}
                  >
                    <X className='w-4 h-4' />
                  </button>
                </span>
              ))}
              <button
                onClick={clearAllFilters}
                className='ml-2 text-sm text-gray-600 hover:text-hexagon'
              >
                Clear all
              </button>
            </div>
          )}

          {/* Multi-select filter groups */}
          <div className='grid grid-cols-1 md:grid-cols-3 gap-3'>
            <div>
              <div className='text-xs font-medium text-gray-500 mb-2'>
                Categories
              </div>
              <div className='flex flex-wrap gap-2'>
                {categories.map(category => {
                  const active = selectedCategories.includes(category);
                  return (
                    <button
                      key={category}
                      onClick={() =>
                        toggleInArray(
                          category,
                          selectedCategories,
                          setSelectedCategories
                        )
                      }
                      className={`px-3 py-1.5 rounded-full text-sm border transition ${
                        active
                          ? 'bg-hexagon text-white border-hexagon'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-hexagon'
                      }`}
                    >
                      {category}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className='text-xs font-medium text-gray-500 mb-2'>
                Difficulty
              </div>
              <div className='flex flex-wrap gap-2'>
                {difficulties.map(difficulty => {
                  const label =
                    difficulty.charAt(0).toUpperCase() + difficulty.slice(1);
                  const active = selectedDifficulties.includes(difficulty);
                  return (
                    <button
                      key={difficulty}
                      onClick={() =>
                        toggleInArray(
                          difficulty,
                          selectedDifficulties,
                          setSelectedDifficulties
                        )
                      }
                      className={`px-3 py-1.5 rounded-full text-sm border transition ${
                        active
                          ? 'bg-hexagon text-white border-hexagon'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-hexagon'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <div className='text-xs font-medium text-gray-500 mb-2'>Tags</div>
              <div className='flex flex-wrap gap-2 max-h-28 overflow-y-auto pr-1'>
                {tags.map(tag => {
                  const active = selectedTags.includes(tag);
                  return (
                    <button
                      key={tag}
                      onClick={() =>
                        toggleInArray(tag, selectedTags, setSelectedTags)
                      }
                      className={`px-3 py-1.5 rounded-full text-sm border transition ${
                        active
                          ? 'bg-hexagon text-white border-hexagon'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-hexagon'
                      }`}
                    >
                      {tag}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Exercises Grid */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.6 }}
        className='space-y-6'
      >
        <h2 className='text-2xl font-bold text-gray-900'>
          Available Exercises
        </h2>

        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
          {filteredExercises.map((exercise, index) => {
            const progress = userProgress.find(
              p => p.exercise_id === exercise.id
            );
            const status = progress?.status || 'not_started';

            const detectedLanguage = detectLanguage(
              exercise.title,
              exercise.category,
              exercise.tags
            );

            return (
              <motion.div
                key={exercise.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className='glass-effect rounded-2xl p-6 hover:shadow-xl transition-all duration-300'
              >
                <div className='flex items-start justify-between mb-4'>
                  <div className='w-12 h-12'>
                    {detectedLanguage ? (
                      <div className='hexagon bg-white flex items-center justify-center p-2 border-2 border-gray-200'>
                        <LanguageIcon language={detectedLanguage} size={32} />
                      </div>
                    ) : (
                      <div className='hexagon bg-gradient-to-r from-hexagon to-accent-500 flex items-center justify-center'>
                        <BookOpen className='w-6 h-6 text-white' />
                      </div>
                    )}
                  </div>
                  {getStatusIcon(status)}
                </div>

                <h3 className='text-lg font-semibold text-gray-900 mb-2'>
                  {formatExerciseTitle(exercise.title)}
                </h3>

                <p className='text-gray-600 text-sm mb-4 line-clamp-2'>
                  {exercise.description}
                </p>

                <div className='flex items-center gap-2 mb-4'>
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(
                      exercise.difficulty
                    )}`}
                  >
                    {exercise.difficulty}
                  </span>
                  <span className='text-gray-500 text-sm'>
                    {exercise.estimated_time} min
                  </span>
                </div>

                <div className='flex items-center justify-between'>
                  <span className='text-sm text-gray-500'>
                    {exercise.category}
                  </span>
                  <Link
                    to={`/exercise/${exercise.id}`}
                    className='inline-flex items-center space-x-1 text-hexagon hover:text-hexagon-dark font-medium text-sm transition-colors duration-200'
                  >
                    <span>Start</span>
                    <ArrowRight className='w-4 h-4' />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>

        {filteredExercises.length === 0 && (
          <div className='text-center py-12'>
            <div className='w-24 h-24 mx-auto mb-4'>
              <div className='hexagon bg-gray-200 flex items-center justify-center'>
                <Search className='w-12 h-12 text-gray-400' />
              </div>
            </div>
            <p className='text-gray-600 text-lg'>
              {exercises.length === 0
                ? 'No exercises in the database yet.'
                : 'No exercises found matching your criteria'}
            </p>
            {exercises.length > 0 ? (
              <button
                onClick={clearAllFilters}
                className='mt-4 text-hexagon hover:text-hexagon-dark font-medium'
              >
                Clear all filters
              </button>
            ) : (
              <p className='mt-4 text-sm text-gray-500'>
                Run migrations to seed sample exercises, or sync from GitHub.
              </p>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default Dashboard;
